import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Activity, 
  Target, 
  CheckCircle, 
  Calendar,
  Trophy,
  TrendingUp,
  Clock,
  Zap
} from 'lucide-react';
import { SparkleGuide } from './guide/SparkleGuide';
import WhyThisMattersCard from '@/components/education/WhyThisMattersCard';
import { useWellnessEducation } from '@/hooks/useWellnessEducation';

interface ExerciseData {
  // Morning Planning
  planned_exercise: string;
  planned_time: string;
  planned_duration: number;
  planned_intensity: number;
  motivation_before: number; // 1-10 scale
  desire_level: number; // 1-10 scale
  
  // Evening Completion
  exercise_completed: boolean;
  actual_exercise: string;
  actual_duration: number;
  actual_intensity: number;
  completion_time: string;
  effort_required: number; // 1-10 scale (how hard was it to do)
  feelings_before: string; // How did you feel before starting
  feelings_after: string; // How did you feel after finishing
  
  // Tracking
  weekly_commitment_score: number;
  personal_promise_kept: boolean;
  notes: string;
}

interface EnhancedExerciseTrackerProps {
  mode: 'morning' | 'evening';
  initialData?: Partial<ExerciseData>;
  onDataChange: (data: ExerciseData) => void;
}

const EXERCISE_TYPES = [
  'Walking',
  'Running/Jogging',
  'Cycling',
  'Swimming', 
  'Yoga',
  'Pilates',
  'Strength Training',
  'HIIT Workout',
  'Dance',
  'Sports (specify)',
  'Stretching',
  'Martial Arts',
  'Rock Climbing',
  'Hiking',
  'None planned'
];

const TIME_SLOTS = [
  'Early Morning (5-7am)',
  'Morning (7-9am)', 
  'Mid-Morning (9-11am)',
  'Lunch Time (11am-1pm)',
  'Afternoon (1-4pm)',
  'Early Evening (4-6pm)',
  'Evening (6-8pm)',
  'Night (8-10pm)',
  'Flexible/When possible'
];

const COMMITMENT_MESSAGES = [
  "Every promise you keep to yourself builds unshakeable self-trust.",
  "Small commitments lead to big transformations.",
  "You're teaching your brain that your word matters.",
  "Consistency beats perfection every time.",
  "Your future self is counting on the choices you make today."
];

export default function EnhancedExerciseTracker({ mode, initialData, onDataChange }: EnhancedExerciseTrackerProps) {
  const [data, setData] = useState<ExerciseData>({
    planned_exercise: '',
    planned_time: '',
    planned_duration: 30,
    planned_intensity: 5,
    motivation_before: 5,
    desire_level: 5,
    exercise_completed: false,
    actual_exercise: '',
    actual_duration: 0,
    actual_intensity: 5,
    completion_time: '',
    effort_required: 5,
    feelings_before: '',
    feelings_after: '',
    weekly_commitment_score: 0,
    personal_promise_kept: false,
    notes: '',
    ...initialData
  });
  
  const { data: education } = useWellnessEducation('exercise_tracking', 'physical-activity');

  React.useEffect(() => {
    // Auto-update promise kept status
    if (mode === 'evening') {
      const promiseKept = data.exercise_completed && 
                         data.planned_exercise !== '' && 
                         data.planned_exercise !== 'None planned';
      const updatedData = { ...data, personal_promise_kept: promiseKept };
      setData(updatedData);
      onDataChange(updatedData);
    } else {
      onDataChange(data);
    }
  }, [data, mode]);

  const handleInputChange = (field: keyof ExerciseData, value: any) => {
    const updatedData = { ...data, [field]: value };
    setData(updatedData);
  };

  const getIntensityLabel = (intensity: number): string => {
    if (intensity <= 2) return 'Very Light';
    if (intensity <= 4) return 'Light';
    if (intensity <= 6) return 'Moderate';
    if (intensity <= 8) return 'Vigorous';
    return 'Very Intense';
  };

  const getCommitmentColor = (completed: boolean) => {
    return completed ? 'text-accent' : 'text-destructive';
  };

  if (mode === 'morning') {
    return (
      <div className="space-y-6">
        <Card className="p-6">
          <div className="text-center mb-6">
            <Target className="w-12 h-12 text-primary mx-auto mb-4" />
            <div className="flex items-center justify-center gap-2 mb-2">
              <h3 className="text-2xl font-bold">Movement Planning</h3>
              <SparkleGuide
                title="How to track your exercise"
                items={[
                  { title: 'Plan in the morning', description: 'Commit to what movement you\'ll do and when - this builds self-trust.' },
                  { title: 'Log in the evening', description: 'Record what you actually did to see your consistency over time.' },
                  { title: 'Track how you feel', description: 'Note feelings before and after to see exercise\'s mood impact.' },
                  { title: 'Build streaks', description: 'Keep your commitment score high to strengthen your habit.' }
                ]}
              />
            </div>
            <p className="text-muted-foreground">
              What movement will you commit to today? When will you do it?
            </p>
          </div>

          {/* Movement Type */}
          <div className="space-y-6">
            <div>
              <Label className="text-lg font-semibold mb-4 block">
                What movement will you do today?
              </Label>
              <Select 
                value={data.planned_exercise} 
                onValueChange={(value) => handleInputChange('planned_exercise', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose your movement" />
                </SelectTrigger>
                <SelectContent>
                  {EXERCISE_TYPES.map((exercise) => (
                    <SelectItem key={exercise} value={exercise}>
                      {exercise}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {data.planned_exercise && data.planned_exercise !== 'None planned' && (
              <>
                {/* When */}
                <div>
                  <Label className="text-lg font-semibold mb-4 block">
                    When will you move?
                  </Label>
                  <Select 
                    value={data.planned_time} 
                    onValueChange={(value) => handleInputChange('planned_time', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose your time" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_SLOTS.map((time) => (
                        <SelectItem key={time} value={time}>
                          {time}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Duration */}
                <div>
                  <Label className="text-lg font-semibold mb-4 block">
                    How long? ({data.planned_duration} minutes)
                  </Label>
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleInputChange('planned_duration', 10)}
                        className={data.planned_duration === 10 ? 'bg-primary text-primary-foreground' : ''}
                      >
                        10 min
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleInputChange('planned_duration', 20)}
                        className={data.planned_duration === 20 ? 'bg-primary text-primary-foreground' : ''}
                      >
                        20 min
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleInputChange('planned_duration', 30)}
                        className={data.planned_duration === 30 ? 'bg-primary text-primary-foreground' : ''}
                      >
                        30 min
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleInputChange('planned_duration', 45)}
                        className={data.planned_duration === 45 ? 'bg-primary text-primary-foreground' : ''}
                      >
                        45 min
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleInputChange('planned_duration', 60)}
                        className={data.planned_duration === 60 ? 'bg-primary text-primary-foreground' : ''}
                      >
                        60 min
                      </Button>
                    </div>
                    <div className="flex items-center gap-3">
                      <Input
                        type="number"
                        min="1"
                        max="300"
                        value={data.planned_duration}
                        onChange={(e) => handleInputChange('planned_duration', parseInt(e.target.value) || 30)}
                        className="w-20"
                      />
                      <span className="text-sm text-muted-foreground">Custom duration</span>
                    </div>
                  </div>
                </div>

                {/* Intensity */}
                <div>
                  <Label className="text-lg font-semibold mb-4 block">
                    Planned intensity (1-10)
                  </Label>
                  <div className="px-4 space-y-3">
                    <div className="flex gap-2">
                      {[1,2,3,4,5,6,7,8,9,10].map((level) => (
                        <Button
                          key={level}
                          variant="outline"
                          size="sm"
                          onClick={() => handleInputChange('planned_intensity', level)}
                          className={`w-10 h-10 ${data.planned_intensity === level ? 'bg-primary text-primary-foreground' : ''}`}
                        >
                          {level}
                        </Button>
                      ))}
                    </div>
                    <div className="text-center">
                      <Badge variant="secondary" className="text-sm">
                        {getIntensityLabel(data.planned_intensity)}
                      </Badge>
                    </div>
                  </div>
                </div>

                {/* Motivation & Desire Check-in */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium mb-2 block">
                      How motivated do you feel right now? (1-10)
                    </Label>
                    <div className="flex gap-1">
                      {[1,2,3,4,5,6,7,8,9,10].map((level) => (
                        <Button
                          key={level}
                          variant="outline"
                          size="sm"
                          onClick={() => handleInputChange('motivation_before', level)}
                          className={`w-10 h-10 ${data.motivation_before === level ? 'bg-primary text-primary-foreground' : ''}`}
                        >
                          {level}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-medium mb-2 block">
                      How much do you want to do this? (1-10)
                    </Label>
                    <div className="flex gap-1">
                      {[1,2,3,4,5,6,7,8,9,10].map((level) => (
                        <Button
                          key={level}
                          variant="outline"
                          size="sm"
                          onClick={() => handleInputChange('desire_level', level)}
                          className={`w-10 h-10 ${data.desire_level === level ? 'bg-primary text-primary-foreground' : ''}`}
                        >
                          {level}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Commitment */}
                <Card className="p-4 bg-gradient-therapy border-soft-accent/30">
                  <div className="flex items-center gap-3 mb-2">
                    <CheckCircle className="w-5 h-5 text-accent" />
                    <h4 className="font-semibold text-primary">Your Commitment</h4>
                  </div>
                  <p className="text-sm text-foreground mb-3">
                    "I commit to {data.planned_exercise.toLowerCase()} for {data.planned_duration} minutes {data.planned_time ? `during ${data.planned_time.toLowerCase()}` : 'today'}."
                  </p>
                  <p className="text-xs text-accent italic">
                    {COMMITMENT_MESSAGES[Math.floor(Math.random() * COMMITMENT_MESSAGES.length)]}
                  </p>
                </Card>
              </>
            )}
          </div>
        </Card>
      </div>
    );
  }

  // Evening Mode
  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="text-center mb-6">
          <Activity className="w-12 h-12 text-primary mx-auto mb-4" />
          <h3 className="text-2xl font-bold mb-2">Movement Check-In</h3>
          <p className="text-muted-foreground">
            Did you follow through on your movement commitment?
          </p>
        </div>

        {/* Planned vs Actual */}
        {data.planned_exercise && data.planned_exercise !== 'None planned' && (
          <div className="mb-6 p-4 bg-muted/50 rounded-lg">
            <h4 className="font-semibold mb-2">Your Morning Commitment:</h4>
            <p className="text-sm text-muted-foreground">
              {data.planned_exercise} for {data.planned_duration} minutes {data.planned_time ? `during ${data.planned_time}` : ''}
            </p>
          </div>
        )}

        {/* Completion Status */}
        <div className="mb-6">
          <Label className="text-lg font-semibold mb-4 block">
            Did you complete your planned movement?
          </Label>
          <div className="flex gap-4">
            <Button
              variant={data.exercise_completed ? "default" : "outline"}
              onClick={() => handleInputChange('exercise_completed', true)}
              className="flex items-center gap-2"
            >
              <CheckCircle className="w-4 h-4" />
              Yes, I did it!
            </Button>
            <Button
              variant={!data.exercise_completed ? "secondary" : "outline"}
              onClick={() => handleInputChange('exercise_completed', false)}
              className="flex items-center gap-2"
            >
              No, I didn't
            </Button>
          </div>
        </div>

        {data.exercise_completed && (
          <div className="space-y-6">
            {/* What did you actually do */}
            <div>
              <Label className="text-sm font-medium mb-2 block">
                What movement did you actually do?
              </Label>
              <Select 
                value={data.actual_exercise} 
                onValueChange={(value) => handleInputChange('actual_exercise', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select actual exercise" />
                </SelectTrigger>
                <SelectContent>
                  {EXERCISE_TYPES.filter(ex => ex !== 'None planned').map((exercise) => (
                    <SelectItem key={exercise} value={exercise}>
                      {exercise}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Actual duration and intensity */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium mb-2 block">Duration (minutes)</Label>
                <Input
                  type="number"
                  min="0"
                  value={data.actual_duration}
                  onChange={(e) => handleInputChange('actual_duration', parseInt(e.target.value) || 0)}
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-2 block">Actual intensity (1-10)</Label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={data.actual_intensity}
                  onChange={(e) => handleInputChange('actual_intensity', parseInt(e.target.value) || 5)}
                />
              </div>
            </div>

            {/* When completed */}
            <div>
              <Label className="text-sm font-medium mb-2 block">When did you exercise?</Label>
              <Select 
                value={data.completion_time} 
                onValueChange={(value) => handleInputChange('completion_time', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select time completed" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map((time) => (
                    <SelectItem key={time} value={time}>
                      {time}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Effort Required */}
            <div>
              <Label className="text-sm font-medium mb-2 block">
                How much effort did it take to do this? (1=easy, 10=very hard)
              </Label>
              <div className="flex gap-1">
                {[1,2,3,4,5,6,7,8,9,10].map((level) => (
                  <Button
                    key={level}
                    variant="outline"
                    size="sm"
                    onClick={() => handleInputChange('effort_required', level)}
                    className={`w-10 h-10 ${data.effort_required === level ? 'bg-primary text-primary-foreground' : ''}`}
                  >
                    {level}
                  </Button>
                ))}
              </div>
            </div>

            {/* Feelings Before & After */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium mb-2 block">
                  How did you feel BEFORE starting?
                </Label>
                <Textarea
                  placeholder="Tired, resistant, anxious, excited..."
                  value={data.feelings_before}
                  onChange={(e) => handleInputChange('feelings_before', e.target.value)}
                  className="min-h-[80px]"
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-2 block">
                  How did you feel AFTER finishing?
                </Label>
                <Textarea
                  placeholder="Energised, accomplished, relieved, proud..."
                  value={data.feelings_after}
                  onChange={(e) => handleInputChange('feelings_after', e.target.value)}
                  className="min-h-[80px]"
                />
              </div>
            </div>
          </div>
        )}

        {/* Notes */}
        <div className="mt-6">
          <Label className="text-sm font-medium mb-2 block">
            How did it feel? Any challenges or wins?
          </Label>
          <Textarea
            placeholder="I felt energised after... / I struggled with motivation but... / Next time I'll..."
            value={data.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            className="min-h-[80px]"
          />
        </div>
      </Card>

      {/* Personal Promise Tracking */}
      <Card className={`p-6 ${data.personal_promise_kept ? 'bg-gradient-therapy border-accent/30' : 'bg-muted/50 border-destructive/30'}`}>
        <div className="flex items-center gap-3 mb-4">
          <Trophy className={`w-6 h-6 ${getCommitmentColor(data.personal_promise_kept)}`} />
          <h4 className={`font-semibold ${getCommitmentColor(data.personal_promise_kept)}`}>
            Personal Promise
          </h4>
        </div>
        
        {data.personal_promise_kept ? (
          <div>
            <p className="text-accent font-medium mb-2">
              🎉 Promise kept! You're building unshakeable self-trust.
            </p>
            <p className="text-sm text-foreground">
              Every commitment you keep to yourself strengthens your relationship with your future self. 
              This is how transformation happens - one promise at a time.
            </p>
          </div>
        ) : (
          <div>
            <p className="text-destructive font-medium mb-2">
              Promise not kept today. That's okay - tomorrow is a new opportunity.
            </p>
            <p className="text-sm text-muted-foreground">
              Self-compassion is key. What got in the way? How can you set yourself up for success tomorrow? 
              Progress, not perfection.
            </p>
          </div>
        )}

        {/* Weekly Commitment Score Placeholder */}
        <div className="mt-4">
          <div className="flex justify-between text-sm text-muted-foreground mb-2">
            <span>This week's commitment score</span>
            <span>4/7 days</span>
          </div>
          <Progress value={57} className="h-2" />
          <p className="text-xs text-muted-foreground mt-1">
            You're showing up for yourself 4 out of 7 days this week. Keep building that momentum!
          </p>
        </div>
      </Card>

      {/* Motivational Message */}
      <Card className="p-4 bg-primary/5 border-primary/20">
        <div className="flex items-center gap-2 mb-2">
          <Zap className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium">Movement Insight</span>
        </div>
        <p className="text-sm text-muted-foreground">
          {data.exercise_completed
            ? "Movement boosts mood, energy, and sleep quality for hours after exercise. Your body is thanking you!"
            : "Movement doesn't have to be perfect or long. Even 10 minutes of movement can shift your energy and mindset."
          }
        </p>
      </Card>
      
      {education && (
        <WhyThisMattersCard
          toolName={education.tool_name}
          shortDescription={education.short_description}
          whyItMatters={education.why_it_matters}
          researchSummary={education.research_summary}
          howToOptimise={education.how_to_optimize}
          researchLinks={education.research_links}
          tags={education.tags}
        />
      )}
    </div>
  );
}